import { cn } from 'cn';
import { Menu, PanelLeftClose, Pin, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { useOrganisation } from '@/api/queries/organisation';
import { useSession } from '@/auth/use-session';
import { Wordmark } from '@/components/brand/wordmark';
import { ThemeToggle } from '@/components/common/theme-toggle';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useBrandTheme } from '@/lib/brand-theme';
import { ADMIN_NAV_ITEMS, NAV_ITEMS } from './navigation';
import { SidebarNav } from './sidebar-nav';
import { useSidebarPeek, useSidebarPinned } from './sidebar-pin';
import { TenantSwitcher } from './tenant-switcher';
import { UserMenu } from './user-menu';
import { ViewingBanner } from './viewing-banner';
import { AttentionBadge } from './attention-badge';
import { NotificationBell } from './notification-bell';
import { WorkspaceSwitcher } from './workspace-switcher';

/**
 * Logged-in shell: on large screens a slim icon rail that opens over the page on hover or
 * keyboard focus, or stays open when pinned (2026-11-02: closed by default); on tablets and
 * phones a slide-out menu. Step 17e: variant "admin" is the Super Admin panel: its own menu,
 * never an organisation's, and Tekansa blue. Tekansa staff only enter an organisation's app
 * through "Log in as".
 */
export function AppLayout({ variant = 'app' }: { variant?: 'app' | 'admin' }) {
  const admin = variant === 'admin';
  const session = useSession();
  const items = admin ? ADMIN_NAV_ITEMS : NAV_ITEMS;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pinned, togglePinned] = useSidebarPinned();
  const peek = useSidebarPeek();
  // The organisation's brand colour (set by Tekansa); Super Admin keeps Tekansa blue.
  const brandColor = useOrganisation(!admin && session.tenant !== null).data?.brandColor;
  useBrandTheme(admin ? null : brandColor);
  const expanded = pinned || peek.open;
  // Escape closes the peeking sidebar (and leaves it, so focus does not re-open it).
  const sidebar = useRef<HTMLElement>(null);
  const closePeek = peek.close;
  useEffect(() => {
    if (pinned || !peek.open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      closePeek();
      const active = document.activeElement;
      if (active instanceof HTMLElement && sidebar.current?.contains(active)) active.blur();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinned, peek.open, closePeek]);
  const fade = cn(
    'transition-opacity duration-150 motion-reduce:transition-none',
    expanded ? 'opacity-100 delay-75' : 'opacity-0',
  );

  return (
    <div className="app-canvas min-h-dvh">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-primary px-3 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>

      <aside
        aria-label="Sidebar"
        data-slot="sidebar"
        data-pinned={pinned}
        data-expanded={expanded}
        ref={sidebar}
        {...(pinned ? {} : peek.handlers)}
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex',
          'transition-[width,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none',
          expanded ? 'w-64' : 'w-[72px]',
          expanded && !pinned && 'shadow-[0_12px_40px_-8px_rgba(15,23,42,0.28)]',
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-2 pr-3 pl-5">
          {expanded ? (
            <>
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <Wordmark />
                {admin && <AdminTag />}
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={(event) => {
                  // Unpinning closes it at once (the mouse is still over it).
                  if (pinned) {
                    peek.close();
                    event.currentTarget.blur();
                  }
                  togglePinned();
                }}
                aria-pressed={pinned}
                aria-label={pinned ? 'Collapse sidebar' : 'Keep sidebar open'}
                title={pinned ? 'Collapse sidebar' : 'Keep sidebar open'}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                {pinned ? <PanelLeftClose aria-hidden="true" /> : <Pin aria-hidden="true" />}
              </Button>
            </>
          ) : (
            <Wordmark compact />
          )}
        </div>
        <div className="flex-1 overflow-x-hidden overflow-y-auto px-3 pt-1 pb-3">
          <SidebarNav
            expanded={expanded}
            items={items}
            onNavigate={pinned ? undefined : peek.close}
          />
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-2 border-t border-sidebar-border/70 px-3 pt-3 pb-3">
          <UserMenu variant="sidebar" collapsed={!expanded} reveal={fade} admin={admin} />
          <p className={cn('px-2 text-[11px] whitespace-nowrap text-muted-foreground/80', fade)}>
            Tekansa Connect · v0.1
          </p>
        </div>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="flex w-72 flex-col bg-sidebar p-0 text-sidebar-foreground"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Main navigation</SheetDescription>
          <div className="flex h-16 items-center px-5">
            <Wordmark />
          </div>
          <div className="px-3 py-3">
            <SidebarNav onNavigate={() => setMobileOpen(false)} items={items} />
          </div>
          <div className="mt-auto px-3 pb-4">
            <UserMenu variant="sidebar" admin={admin} />
          </div>
        </SheetContent>
      </Sheet>

      <div
        className={cn(
          'transition-[padding] duration-200 ease-out',
          pinned ? 'lg:pl-64' : 'lg:pl-[72px]',
          'print:pl-0!',
        )}
      >
        <header data-app-header className="flex h-14 items-center gap-2 px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu aria-hidden="true" />
          </Button>
          <span className="lg:hidden">
            <Wordmark />
          </span>
          {admin && (
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <ShieldCheck aria-hidden="true" className="size-4 text-primary" /> Super Admin
            </span>
          )}
          <div className="ml-auto flex min-w-0 items-center gap-1">
            {!admin && (
              <>
                <TenantSwitcher />
                <WorkspaceSwitcher />
                <AttentionBadge />
                <NotificationBell />
              </>
            )}
            <ThemeToggle />
          </div>
        </header>
        <ViewingBanner />
        <main
          id="main"
          tabIndex={-1}
          className="mx-auto w-full max-w-[1360px] px-4 pt-2 pb-8 outline-none sm:px-6 lg:px-8"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function AdminTag() {
  return (
    <span className="shrink-0 rounded-md bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
      Super Admin
    </span>
  );
}
